import React, { useState, useEffect } from 'react';
import Form from '../common/Form';
import FormSection from '../common/FormSection';
import FormField from '../common/FormField';
import {
  ActivitySpeciesDTO,
  ActivityTypeDTO,
  ActivityCategoryDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useActivityTypes } from '../../hooks/useActivityTypeApi';
import { useActivityCategories } from '../../hooks/useActivityCategoryApi';

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  },
  settings: {
    id: 'settings',
    title: 'Settings'
  }
} as const;

interface ActivitySpeciesTypeFormProps {
  initialValues: ActivitySpeciesDTO;
  onSubmit: (data: ActivitySpeciesDTO) => void;
  onCancel: () => void;
}

const ActivitySpeciesTypeForm: React.FC<ActivitySpeciesTypeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ActivitySpeciesDTO>(initialValues);
  const [selectedTypeId, setSelectedTypeId] = useState<string>(initialValues.activityTypeId || '');

  const { data: activityTypes = [] } = useActivityTypes();
  const { data: activityCategories = [], isLoading: isLoadingActivityCategories } = useActivityCategories();

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
    activityTypeId: [{ required: true, message: 'Activity Type is required' }],
    activityCategoryId: [{ required: true, message: 'Activity Category is required' }],
  };

  const { errors, validate } = useFormValidation<ActivitySpeciesDTO>(validationRules);

  // Reusable function to fetch categories
  const fetchCategories = async (typeId: string) => {
    if (!typeId) {
      setActivityCategories([]);
      return;
    }
    try {
      const response = await activityCategoryApi.search({
        filters: {
          activityTypeId: typeId,
          isActive: true,
        },
      });
      if (response?.data) {
        setActivityCategories(response.data.results ?? []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Update categories when type is selected
  useEffect(() => {
    if (selectedTypeId) {
      fetchCategories(selectedTypeId);
    }
  }, [selectedTypeId]);

  // Add effect to handle initial load
  useEffect(() => {
    const initializeForm = async () => {
      if (initialValues.activityTypeId && initialValues.activityCategoryId) {
        setSelectedTypeId(initialValues.activityTypeId);
        await fetchCategories(initialValues.activityTypeId);
      }
    };
    initializeForm();
  }, [initialValues]);

  // Handle type change
  const handleTypeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const newTypeId = e.target.value;
    setSelectedTypeId(newTypeId);
    setFormData(prev => ({ 
      ...prev, 
      activityTypeId: newTypeId,
      activityCategoryId: '' // Reset category when type changes
    }));
  };

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection 
        id={FORM_SECTIONS.basicInfo.id} 
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="activityTypeId"
          label="Activity Type"
          type="dropdown"
          value={selectedTypeId}
          onChange={handleTypeChange}
          placeholder="Select Activity Type"
          required={true}
          error={errors.activityTypeId}
          options={activityTypes.map((type) => ({
            value: type.id,
            label: type.title,
          }))}
        />
        <FormField
          id="activityCategoryId"
          label="Activity Category"
          type="dropdown"
          value={formData.activityCategoryId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Activity Category"
          required={true}
          error={errors.activityCategoryId}
          options={activityCategories.map((category) => ({
            value: category.id,
            label: category.title,
          }))}
          disabled={!selectedTypeId}
        />
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
      </FormSection>

      <FormSection 
        id={FORM_SECTIONS.settings.id} 
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Display Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          required={true}
          error={errors.displayPosition}
          helper="Lower numbers will be displayed first"
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active"
        />
      </FormSection>
    </Form>
  );
};

export default ActivitySpeciesTypeForm;